import { useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";
import { api, formatErr } from "../lib/api";
import { DEPARTMENTS, genPassword } from "../lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const EMPTY = { name: "", email: "", role: "employee", department: "", designation: "", phone: "", date_of_joining: "", ctc_annual: "", pan: "", uan: "", bank_account: "", bank_ifsc: "", entity_id: "", manager_id: "" };
const FIELDS = [["name", "Full name"], ["email", "Work email"], ["designation", "Designation"], ["phone", "Phone"], ["date_of_joining", "Date of joining", "date"], ["ctc_annual", "Annual CTC (₹)", "number"], ["pan", "PAN"], ["uan", "UAN (PF)"], ["bank_account", "Bank account no."], ["bank_ifsc", "Bank IFSC"]];

export const EmployeeForm = ({ open, onOpenChange, employee, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [ents, setEnts] = useState([]);
  const [emps, setEmps] = useState([]);

  useEffect(() => {
    if (open) {
      api.get("/entities").then((r) => setEnts(r.data));
      api.get("/employees").then((r) => setEmps(r.data.filter((e) => e.status === "active")));
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      setF(employee ? { ...EMPTY, ...employee, ctc_annual: employee.ctc_annual || "" } : EMPTY);
      setPw(genPassword());
    }
  }, [open, employee]);

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = { ...f, ctc_annual: Number(f.ctc_annual) || 0 };
    try {
      if (employee) await api.put(`/employees/${employee.id}`, body);
      else await api.post("/employees", { ...body, temp_password: pw });
      toast.success(employee ? "Employee updated" : `Employee created. Temporary password: ${pw}`, { duration: 10000 });
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white" data-testid="employee-form-dialog">
        <DialogHeader><DialogTitle>{employee ? "Edit employee" : "Add employee"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4">
          {FIELDS.map(([k, l, t]) => (
            <div key={k} className="space-y-1.5">
              <Label>{l}</Label>
              <Input type={t || (k === "email" ? "email" : "text")} required={k === "name" || k === "email"} disabled={k === "email" && !!employee}
                value={f[k] ?? ""} onChange={set(k)} data-testid={`emp-${k}-input`} />
            </div>
          ))}
          <div className="space-y-1.5">
            <Label>Department</Label>
            <Select value={f.department || undefined} onValueChange={(v) => setF({ ...f, department: v })}>
              <SelectTrigger data-testid="emp-department-select"><SelectValue placeholder="Select department" /></SelectTrigger>
              <SelectContent>{DEPARTMENTS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Access role</Label>
            <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}>
              <SelectTrigger data-testid="emp-role-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="employee">Employee</SelectItem><SelectItem value="admin">HR / Admin</SelectItem></SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Company / entity</Label>
            <Select value={f.entity_id || undefined} onValueChange={(v) => setF({ ...f, entity_id: v })}>
              <SelectTrigger data-testid="emp-entity-select"><SelectValue placeholder="Default entity" /></SelectTrigger>
              <SelectContent>{ents.map((x) => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Reporting manager (approves leave first)</Label>
            <Select value={f.manager_id || "none"} onValueChange={(v) => setF({ ...f, manager_id: v === "none" ? "" : v })}>
              <SelectTrigger data-testid="emp-manager-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No manager (goes straight to HR)</SelectItem>
                {emps.filter((x) => x.id !== employee?.id).map((x) => <SelectItem key={x.id} value={x.id}>{x.name} ({x.employee_code})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {!employee && (
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Temporary password (share with the employee)</Label>
              <div className="flex gap-2">
                <Input value={pw} onChange={(e) => setPw(e.target.value)} data-testid="emp-temp-password-input" className="font-mono" />
                <Button type="button" variant="outline" onClick={() => setPw(genPassword())} data-testid="emp-regen-password-button"><RefreshCw className="h-4 w-4" /></Button>
              </div>
            </div>
          )}
          <div className="sm:col-span-2 flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="emp-cancel-button">Cancel</Button>
            <Button type="submit" disabled={busy} data-testid="emp-save-button" className="bg-[#E08A1E] hover:bg-[#C87515] text-white">{busy ? "Saving..." : "Save"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
